import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { dateFR } from "@/lib/print";

/**
 * Cloche de rappels visible sur TOUTES les pages (barre du haut), et non plus
 * seulement dans l'onglet Agenda. Avant, un rappel de rendez-vous (J-2, J-1,
 * jour J) n'était visible que si l'utilisateur pensait à ouvrir l'Agenda —
 * s'il ne l'ouvrait pas ce jour-là, il ne voyait jamais le rappel. Il est
 * désormais chargé dès l'ouverture du logiciel (juste après connexion) et
 * rafraîchi périodiquement, quelle que soit la page affichée.
 *
 * En plus de la cloche, chaque rappel non encore vu déclenche une notification
 * "popup" (toast) dès l'ouverture de l'application, pour qu'il soit impossible
 * de le manquer même sans cliquer sur la cloche ou ouvrir l'Agenda.
 */
export default function AgendaNotificationsBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  // Mémorise les rappels déjà présentés en popup dans CETTE session du
  // navigateur, pour ne pas re-notifier à chaque rafraîchissement (toutes les
  // 5 min) ceux déjà vus — seuls les nouveaux rappels non encore affichés
  // déclenchent une nouvelle popup.
  const shownIdsRef = useRef(new Set());
  // IDs des popups (toasts) actuellement affichés à l'écran, pour pouvoir
  // les faire disparaître d'un coup dès qu'on clique sur la cloche.
  const activeToastIdsRef = useRef([]);
  // Associe l'id d'un rappel à l'id de sa popup encore affichée, pour pouvoir
  // fermer cette popup précise dès qu'il est marqué comme lu.
  const toastIdByNotificationRef = useRef({});

  // Marque un rappel comme lu (utilisé à la fois par le bouton dans la liste
  // de la cloche et par le bouton "Marquer comme lu" directement dans la
  // popup) : le supprime de la liste ET fait disparaître sa popup si elle est
  // encore affichée à l'écran.
  const markNotificationRead = async (id) => {
    try {
      await api.post(`/agenda/notifications/${id}/read`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      const toastId = toastIdByNotificationRef.current[id];
      if (toastId != null) {
        toast.dismiss(toastId);
        activeToastIdsRef.current = activeToastIdsRef.current.filter((tid) => tid !== toastId);
        delete toastIdByNotificationRef.current[id];
      }
    } catch {
      // silencieux : au pire le rappel réapparaîtra au prochain rafraîchissement
    }
  };

  const load = (isFirstLoad) => {
    api.get("/agenda/notifications", { params: { unread: 1 } })
      .then((r) => {
        setNotifications(r.data);
        const toShow = r.data.filter((n) => !shownIdsRef.current.has(n.id));
        toShow.forEach((n) => {
          shownIdsRef.current.add(n.id);
          const when = `${n.appointment_date ? dateFR(n.appointment_date) : ""}${n.appointment_time ? ` à ${n.appointment_time}` : ""}`;
          const toastId = toast.message(`Rappel : ${n.appointment_title || "rendez-vous"}${when ? ` — ${when}` : ""}`, {
            duration: 10000,
            action: { label: "Voir l'Agenda", onClick: () => navigate("/agenda") },
            cancel: { label: "Marquer comme lu", onClick: () => markNotificationRead(n.id) },
            onDismiss: () => {
              activeToastIdsRef.current = activeToastIdsRef.current.filter((id) => id !== toastId);
              delete toastIdByNotificationRef.current[n.id];
            },
            onAutoClose: () => {
              activeToastIdsRef.current = activeToastIdsRef.current.filter((id) => id !== toastId);
              delete toastIdByNotificationRef.current[n.id];
            },
          });
          activeToastIdsRef.current.push(toastId);
          toastIdByNotificationRef.current[n.id] = toastId;
        });
      })
      .catch(() => {});
  };

  // Fait disparaître immédiatement toutes les popups de rappel encore
  // affichées dès qu'on clique sur la cloche, plutôt que d'attendre qu'elles
  // se referment toutes seules après 10 secondes.
  const dismissActiveToasts = () => {
    activeToastIdsRef.current.forEach((id) => toast.dismiss(id));
    activeToastIdsRef.current = [];
  };

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 5 * 60 * 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const markRead = (id, e) => {
    e.stopPropagation();
    markNotificationRead(id);
  };

  const goToAgenda = () => {
    setOpen(false);
    navigate("/agenda");
  };

  const count = notifications.length;

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => {
          dismissActiveToasts();
          setOpen((o) => !o);
        }}
        className="relative p-2 rounded-full text-gray-500 hover:text-sky-700 hover:bg-sky-50"
        title="Rappels de rendez-vous"
        data-testid="global-reminders-bell"
      >
        <Bell className="w-5 h-5" strokeWidth={1.8} />
        {count > 0 && (
          <span
            data-testid="global-reminders-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold text-white bg-red-600"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="px-3 py-2 border-b border-gray-100 text-sm font-semibold text-gray-800 flex items-center justify-between">
            <span>Rappels de rendez-vous</span>
            {count > 0 && <span className="text-xs text-gray-400">{count} non lu(s)</span>}
          </div>
          {count === 0 ? (
            <div className="px-3 py-6 text-sm text-gray-400 text-center">Aucun rappel pour le moment</div>
          ) : (
            notifications.map((n) => (
              <button
                type="button"
                key={n.id}
                onClick={goToAgenda}
                className="w-full text-left px-3 py-2.5 border-b border-gray-50 last:border-b-0 hover:bg-sky-50 flex items-start justify-between gap-2"
                data-testid="global-reminder-row"
              >
                <div className="text-sm text-gray-700">
                  <div className="font-medium">{n.appointment_title || "Rendez-vous"}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {dateFR(n.appointment_date)} {n.appointment_time ? `à ${n.appointment_time}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => markRead(n.id, e)}
                  className="shrink-0 p-1 text-gray-400 hover:text-emerald-600"
                  title="Marquer comme lu"
                  data-testid="global-reminder-mark-read"
                >
                  <Check className="w-4 h-4" />
                </button>
              </button>
            ))
          )}
          <div className="px-3 py-2 border-t border-gray-100 text-center">
            <button type="button" onClick={goToAgenda} className="text-xs text-sky-700 hover:underline">
              Voir tout dans l'Agenda
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
